import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from 'react-query'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { EquinomBin, ISilo } from 'types'
import { DeleteIcon, EditIcon } from 'components'
import { Tag } from 'components/ui/Tag/Tag'
import { CrudAction, Entity } from 'types/permissions'
import { useSilosService } from 'services/silosService'
import { usePermissions } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import useEditSiloModal from '../dialogs/useEditSiloModal'
import useDeleteSiloModal from '../dialogs/useDeleteSiloModal'
import { SilosItem } from './SilosItem'
import * as sxStyles from './Silos.styled'

interface ISiloWrapper {
  item: ISilo
  data: EquinomBin[]
  activeItem: number
  reFetchingSilos: () => void
}

export const SiloWrapper: React.FC<ISiloWrapper> = ({
  item,
  data,
  activeItem,
  reFetchingSilos,
}) => {
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()
  const [editingItem, setEditingItem] = useState<ISilo | null>(null)
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null)
  const { updateSilo, deleteSilo } = useSilosService()
  const activeBin = data[activeItem]
  const sortedBins = data?.sort(function (a, b) {
    return a?.id - b?.id
  })
  const formatIdToBinName =
    sortedBins &&
    sortedBins.reduce((acc, item) => {
      acc[item?.id] = item.name
      return acc
    }, {})

  const { mutateAsync: updateSiloMutation } = useMutation(updateSilo, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['silos_list'])
      successToast('Silo updated')
      reFetchingSilos()
    },
    onError: (error: { detail?: string }) => {
      toast.error(
        error?.detail ? error.detail : 'An error accured while updating silo'
      )
    },
  })

  const { mutate: deleteSiloMutation } = useMutation(deleteSilo, {
    onSuccess: (data) => {
      setDeleteItemId(null)
      void queryClient.invalidateQueries(['silos_list'])
      successToast(data?.detail ? data.detail : 'Silo deleted successfully')
      reFetchingSilos()
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : 'Silo cannot be deleted')
    },
  })

  const [editModal, toggleEditModal] = useEditSiloModal({
    initialData: editingItem,
    mode: 'edit',
    onConfirm: async (data: ISilo) => {
      try {
        await updateSiloMutation(data)
        setEditingItem(null)
        toggleEditModal()
        reFetchingSilos()
      } catch (error) {
        console.error('Failed to update wheat code:', error)
      }
    },
  })

  const [deleteModal, toggleDeleteModal] = useDeleteSiloModal({
    children: (
      <>
        {item?.bin_id !== null ? (
          <Box sx={sxStyles.deleteWithWarningBox}>
            <WarningRoundedIcon />
            <Typography sx={sxStyles.deleteModalWarningTypography}>
              This silo is connected to bin
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        <Typography sx={sxStyles.deleteModalTypography}>
          Are you sure you want to remove this silo?
        </Typography>
      </>
    ),
    onConfirm: () => {
      if (deleteItemId !== null) {
        void deleteSiloMutation(deleteItemId)
      }
    },
  })

  return (
    <Box sx={sxStyles.silosBoxWrapper}>
      {item?.bin_id !== null ? (
        <Tooltip
          placement="top"
          title={`This silo already attached to ${formatIdToBinName[item?.bin_id]} bin`}
        >
          <Box sx={sxStyles.attachedSiloBoxWrapper}>
            <SilosItem
              {...item}
              binId={activeBin?.id}
              isDisabled={item?.bin_id !== activeBin.id}
            >
              {item?.name}
              {item?.bin_id !== activeBin?.id ? (
                <Tag title={formatIdToBinName[item?.bin_id]} />
              ) : (
                <></>
              )}
            </SilosItem>
          </Box>
        </Tooltip>
      ) : (
        <>
          <SilosItem {...item} binId={activeBin?.id} isDisabled={false}>
            {item?.name}
          </SilosItem>
        </>
      )}
      <Box sx={sxStyles.actionsBoxWrapper}>
        {checkPermission(Entity.SILOS, CrudAction.UPDATE) && (
          <IconButton
            sx={sxStyles.iconBtnStyle}
            onClick={() => {
              setEditingItem(item)
              toggleEditModal()
            }}
          >
            <EditIcon size={16} />
          </IconButton>
        )}
        {checkPermission(Entity.SILOS, CrudAction.DELETE) && (
          <IconButton
            sx={sxStyles.iconBtnStyle}
            onClick={() => {
              if (!item?.id) return
              setDeleteItemId(item.id)
              toggleDeleteModal()
            }}
          >
            <DeleteIcon size={20} />
          </IconButton>
        )}
      </Box>
      {deleteModal}
      {editModal}
    </Box>
  )
}
